import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  BoxV2 as Box,
  ButtonV2 as Button,
  Flex,
  MultimediaUploader,
  MultimediaList,
  AttachmentInfo,
  AttachmentPreview,
  SxProps,
  Tooltip,
} from 'portal-commons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEllipsis,
  faImage,
  faPlus,
  faUpload,
} from '@fortawesome/pro-regular-svg-icons';
import { ContentBlockHeader } from '@/components';
import TwoColumnGrid from './TwoColumnGrid';
import { AttachmentFolder, attachmentController } from '@/apis/core';
import { generateAndDownloadFile } from '@/utils/download';
import { MAX_FILE_SIZE, MAX_SUPPORTING_DOCUMENT_COUNT } from '../../constants';
import UploadSizeExceededModal from '../UploadSizeExceededModal';
import { FileFormats } from '../../types';

interface SupportingDocumentSectionProps {
  setSupportingDocumentUuid: (uuids: string[]) => void;
}

// Custom Multimedia list style
const customMultimediaListStyles: SxProps = {
  '& div[data-testid="multimediaList"] > div': {
    color: '#00667D',
    backgroundColor: '#E6F4F7',
    pl: '16px',
  },
  '& li[data-testid="multimediaListAttachment"]': {
    pl: '16px',
  },
  '& div[data-testid="attachmentFileName"]': {
    fontSize: '14px',
  },
};

const SupportingDocumentSection: FC<SupportingDocumentSectionProps> = ({
  setSupportingDocumentUuid,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [supportedFormats, setSupportedFormats] = useState<FileFormats>({});
  const [uploading, setUploading] = useState(false);
  const [attachments, setAttachments] = useState<AttachmentInfo[]>([]);
  const [previewAttachment, setPreviewAttachment] = useState<AttachmentInfo>();
  const [fetchingFile, setFetchingFile] = useState(false);
  const [sizeExceeded, setSizeExceeded] = useState(false);

  useEffect(() => {
    const loadFormat = async (folder: string) => {
      const response = await attachmentController.getSupportedFileFormats(
        folder
      );
      if (response) {
        setSupportedFormats(response);
      }
    };

    loadFormat(AttachmentFolder.SupportingDocument);
  }, []);

  const customAdditionalBlock = useMemo(() => {
    const supportedFileExtensions = supportedFormats
      ? Object.keys(supportedFormats)
      : ['.jpg', '.png', '.pdf'];
    const supportedFileTypesTooltip = `Supported file types: ${supportedFileExtensions
      .sort()
      .join(', ')}`;
    return (
      <Flex
        sx={{
          flexDirection: 'column',
          alignItems: 'center',
          fontSize: 'H200',
          fontWeight: 400,
          lineHeight: 1.2,
          color: 't.grey300',
          textAlign: 'center',
        }}
      >
        <span>Maximum file size: 10 MB</span>
        <span>
          Approved file types, JPG, PNG, PDF,{' '}
          <Tooltip title={supportedFileTypesTooltip}>
            <FontAwesomeIcon icon={faEllipsis} style={{ color: '#808789' }} />
          </Tooltip>
        </span>
      </Flex>
    );
  }, [supportedFormats]);

  const handleFileChange = (selectedFile: File) => {
    if (selectedFile.size > MAX_FILE_SIZE) {
      setSizeExceeded(true);
      setFile(null);
    } else {
      setFile(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (file && attachments.length < MAX_SUPPORTING_DOCUMENT_COUNT) {
      setUploading(true);
      const attachment = await attachmentController.uploadAttachment({
        folder: AttachmentFolder.SupportingDocument,
        file,
      });
      if (attachment) {
        const newAttachments = [
          ...attachments,
          {
            ...attachment,
            file,
          },
        ];
        setAttachments(newAttachments);
        setSupportingDocumentUuid(newAttachments.map((a) => a.uuid));
      }
      setUploading(false);
      setFile(null);
    }
  };

  const handleSelectAttachment = async (index: number) => {
    if (fetchingFile) return;

    const attachment = attachments[index];
    if (attachment.file) {
      setPreviewAttachment(attachment);
    } else {
      setFetchingFile(true);
      const attachmentFile = await attachmentController.downloadAttachment({
        uuid: attachment.uuid,
      });
      if (attachmentFile) {
        setPreviewAttachment({
          ...attachment,
          file: attachmentFile,
        });
      }
      setFetchingFile(false);
    }
  };

  const handleDownloadAttachment = async (index: number) => {
    if (fetchingFile) return;

    let attachmentFile = attachments[index]?.file;
    if (!attachmentFile) {
      setFetchingFile(true);
      attachmentFile = await attachmentController.downloadAttachment({
        uuid: attachments[index].uuid,
      });
      setFetchingFile(false);
    }
    if (attachmentFile) {
      generateAndDownloadFile(attachmentFile, attachments[index].fileName);
    }
  };

  const handleDeleteAttachment = async (index: number) => {
    const attachment = attachments[index];
    const originalAttachments = [...attachments];

    // Optimistic update: Remove from UI immediately upon click for smooth, lag-free feedback
    const newAttachments = attachments.filter((_, i) => i !== index);
    setAttachments(newAttachments);
    setSupportingDocumentUuid(newAttachments.map((a) => a.uuid));

    const response = await attachmentController.deleteAttachment(
      attachment.uuid
    );
    if (!response) {
      // Defensive rollback: Automatically revert to the original file state if the API fails
      setAttachments(originalAttachments);
      setSupportingDocumentUuid(originalAttachments.map((a) => a.uuid));
    }
  };

  return (
    <Flex
      sx={{
        flexDirection: 'column',
        gap: 'xl',
        width: '100%',
        cursor: fetchingFile || uploading ? 'wait' : 'default',
      }}
    >
      <ContentBlockHeader
        title="Multimedia Upload for Supporting Documents"
        subTitle="Provides an area to upload any supporting information for opt in, call-to-action, terms and conditions, privacy policy, etc."
        icon={faImage}
        rightAction={
          <Button
            type="button"
            shape="square"
            variant="outline"
            startAdornment={faPlus}
            size="sm"
            onClick={handleUpload}
            disabled={!file || uploading}
            data-testid="addSampleMultimedia"
          >
            Add Sample Multimedia
          </Button>
        }
        sx={{ mb: 0 }}
      />

      <TwoColumnGrid>
        <MultimediaUploader
          icon={<FontAwesomeIcon icon={faUpload} size="2x" />}
          disabled={
            attachments.length >= MAX_SUPPORTING_DOCUMENT_COUNT || uploading
          }
          disabledText={
            uploading
              ? 'Uploading...'
              : `Max ${MAX_SUPPORTING_DOCUMENT_COUNT} uploads per program is allowed`
          }
          buttonText="Browse from device"
          description="Drop a multimedia file to upload, or"
          onChange={handleFileChange}
          additional={customAdditionalBlock}
          supportedMimeTypes={supportedFormats}
          data-testid="multimediaUploader"
          style={{
            minWidth: '220px',
            minHeight: '190px',
            padding: 30,
            justifyContent: 'center',
            gap: '10px',
            border: '1px dashed #949495',
            borderRadius: '8px',
          }}
        />
        <Box sx={customMultimediaListStyles}>
          <MultimediaList
            editable
            title="MULTIMEDIA FILES"
            emptyMessage=""
            attachments={attachments}
            onSelect={handleSelectAttachment}
            onDownload={handleDownloadAttachment}
            onDelete={handleDeleteAttachment}
            data-testid="multimediaList"
            style={{
              minWidth: '220px',
              minHeight: '190px',
              border: '1px solid #CCCFD0',
              borderRadius: '8px',
            }}
          />
        </Box>
      </TwoColumnGrid>
      {previewAttachment && previewAttachment.file && (
        <AttachmentPreview
          onClose={() => setPreviewAttachment(undefined)}
          file={previewAttachment.file}
          mimeType={previewAttachment.mimeType}
          data-testid="attachmentPreview"
          style={{ zIndex: 5 }}
        />
      )}
      <UploadSizeExceededModal
        open={sizeExceeded}
        onClose={() => {
          setSizeExceeded(false);
        }}
      />
    </Flex>
  );
};

export default SupportingDocumentSection;
